/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import './TransactionsPage.scss';

import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { ReactComponent as Filter } from 'assets/filter.svg';
import PrintButton from 'components/shared/PrintButton/PrintButton';
import { PageHeader } from '../Header/PageHeader';
import { SectionHeader } from '../Header/SectionHeader';
import TransactionHistoryFilters from 'components/layouts/Transaction/TransactionHistoryFilters';
import MobileTransactionHistoryFilters from 'components/layouts/Transaction/MobileTransactionHistoryFilters';
import TransactionHistoryTable from 'components/layouts/Transaction/TransactionHistoryTable';
import MobileTransactionHistoryTable from 'components/layouts/Transaction/MobileTransactionHistoryTable';
import TransactionPendingTable from 'components/layouts/Transaction/TransactionPendingTable';
import MobileTransactionPendingTable from 'components/layouts/Transaction/MobileTransactionPendingTable';
import Toggle from 'components/shared/ToggleRP';
import HeaderIcon from 'components/shared/HeaderIcon/HeaderIcon';
import { downloadCSV } from 'services/Export/DownloadData';
import { csvFileNameFormat } from 'services/Formats';
import { getTransactionsForExport } from 'services/Export/ExportTransactions';
import {
  getHistoricalTransactionTable,
  getGroupsForHistoryTransactions,
  getGroupsForPendingTransactions,
  getSortedPendingTransactions
} from 'state/transaction/TransactionSelectors';
import LegalCopy from 'components/shared/LegalCopy/LegalCopy';
import Content from 'services/Content';
import { GridExportToCSV } from 'services/TableUtils';
import { hasAllViewOnlyBrokerage } from 'state/view/ViewSelectors';
import { shouldRefreshContactMethodInformation } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import { getContactMethodMaintenance } from 'services/Rest';
import { getBrokerageAccountDescriptors } from 'state/account/AccountSelectors';
import { isMoneyMarketCutoffEnabled } from 'services/FeatureFlags';

export const TransactionsPage = ({
  accounts,
  hasCollegeSavingsOnly,
  hasBrokerage,
  historyTransactionRows,
  historyTransactionGroups,
  isDesktopLayout,
  ownedBrokerageAccounts,
  pendingTransactionRows,
  pendingTransactionGroups,
  isViewOnlyBrokerage,
  refreshContactMethodInfo
}) => {
  const [calledToRefresh, setCalledToRefresh] = useState(false);

  useEffect(() => {
    if (refreshContactMethodInfo && !calledToRefresh) {
      getContactMethodMaintenance();
      setCalledToRefresh(true);
    }
  }, [refreshContactMethodInfo]);
  return (
    <Fragment>
      <div className="transactions-page">
        <PageHeader title="Activity">
          <PrintButton
            handlePrint={window.print}
            isDesktopLayout={isDesktopLayout}
            className="transactions-page"
          />
        </PageHeader>
        <Fragment>
          {isDesktopLayout ? (
            <Fragment>
              <SectionHeader
                title="Pending Transactions"
                hasBrokerage={hasBrokerage}
                accounts={ownedBrokerageAccounts}
                className="transactions-page-pending-header"
                isViewOnlyBrokerage={isViewOnlyBrokerage}
              />
              <TransactionPendingTable
                rows={pendingTransactionRows}
                hasCollegeSavingsOnly={hasCollegeSavingsOnly}
              />
              <div className="history-title-section">
                <SectionHeader
                  title="Transaction History"
                  className="transactions-page-history-header"
                />
                {isDesktopLayout && (
                  <GridExportToCSV
                    buttonClass="header-button export-title"
                    onClick={() =>
                      downloadCSV(
                        getTransactionsForExport(historyTransactionRows),
                        csvFileNameFormat('transaction_history')
                      )
                    }
                  />
                )}
              </div>
              <TransactionHistoryFilters />
              <TransactionHistoryTable
                rows={historyTransactionRows}
                accounts={accounts}
                hasCollegeSavingsOnly={hasCollegeSavingsOnly}
              />
            </Fragment>
          ) : (
            <Fragment>
              <SectionHeader
                hasBrokerage={hasBrokerage}
                accounts={ownedBrokerageAccounts}
                title="Pending Transactions"
                className="transactions-page-pending-header"
                isViewOnlyBrokerage={isViewOnlyBrokerage}
              />
              <MobileTransactionPendingTable
                groups={pendingTransactionGroups}
                hasCollegeSavingsOnly={hasCollegeSavingsOnly}
              />
              <Toggle>
                {({ on, setOn, setOff }) => (
                  <Fragment>
                    <div className="history-title-section">
                      <SectionHeader
                        title="Transaction History"
                        className="transactions-page-history-header"
                      />
                      {isDesktopLayout && (
                        <GridExportToCSV
                          buttonClass="header-button export-title"
                          onClick={() =>
                            downloadCSV(
                              getTransactionsForExport(historyTransactionRows),
                              csvFileNameFormat('transaction_history')
                            )
                          }
                        />
                      )}
                      <HeaderIcon
                        onClick={on ? setOff : setOn}
                        icon={Filter}
                        label="Filter"
                        classPrefix="transactions-page"
                      />
                    </div>
                    {on && (
                      <MobileTransactionHistoryFilters
                        close={setOff}
                        isOpen={on}
                      />
                    )}
                  </Fragment>
                )}
              </Toggle>
              <MobileTransactionHistoryTable
                groups={historyTransactionGroups}
                hasCollegeSavingsOnly={hasCollegeSavingsOnly}
              />
            </Fragment>
          )}
          <LegalCopy
            content={
                 (isMoneyMarketCutoffEnabled() ? Content.transactionsMoneyMarketLegalCopy : Content.transactionsLegalCopy)
            }
          />
        </Fragment>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  accounts: state.accounts,
  ownedBrokerageAccounts: getBrokerageAccountDescriptors(state),
  hasCollegeSavingsOnly: state.view.hasCollegeSavingsOnly,
  hasBrokerage: state.view.hasBrokerage,
  isDesktopLayout: state.view.isDesktopLayout,
  historyTransactionRows: getHistoricalTransactionTable(state),
  historyTransactionGroups: getGroupsForHistoryTransactions(state),
  pendingTransactionRows: getSortedPendingTransactions(state),
  pendingTransactionGroups: getGroupsForPendingTransactions(state),
  isViewOnlyBrokerage: hasAllViewOnlyBrokerage(state),
  refreshContactMethodInfo: shouldRefreshContactMethodInformation(state)
});

export default withRouter(connect(mapStateToProps)(TransactionsPage));
